// src/components/SubscriptionStatus.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { formatDate } from '../utils/dateUtils';
import { getSubscription } from '../services/subscriptionService';
import SubscriptionManager from './SubscriptionManager';

/**
 * Component to display subscription status in the user dashboard
 */
function SubscriptionStatus() {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showManager, setShowManager] = useState(false);

  // Fetch subscription data
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        if (currentUser && currentUser.uid) {
          setIsLoading(true);
          const subscriptionData = await getSubscription(currentUser.uid);
          setSubscription(subscriptionData);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, [currentUser]);

  // Handle subscription update or cancellation
  const handleSubscriptionChange = (updatedSubscription) => {
    setSubscription(updatedSubscription);
    setShowManager(false);
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
      </div>
    );
  }

  // Determine subscription status message and appearance
  const getStatusDisplay = () => {
    if (!subscription) {
      return {
        badge: 'Free',
        badgeColor: 'bg-gray-200 text-gray-800',
        message: 'You are currently using the free version',
        className: 'border-gray-200'
      };
    }

    switch (subscription.status) {
      case 'active':
        return {
          badge: 'Active',
          badgeColor: 'bg-green-100 text-green-800',
          message: `Your subscription is active until ${formatDate(subscription.endDate)}`,
          className: 'border-green-200'
        };
        
      case 'past_due':
        return {
          badge: 'Past Due',
          badgeColor: 'bg-yellow-100 text-yellow-800',
          message: 'Your payment is past due. Please update your payment method.',
          className: 'border-yellow-200'
        };

      case 'canceled':
        return {
          badge: 'Canceled',
          badgeColor: 'bg-red-100 text-red-800',
          message: `Your access will end on ${formatDate(subscription.endDate)}`,
          className: 'border-red-200'
        };

      case 'unpaid':
        return {
          badge: 'Unpaid',
          badgeColor: 'bg-orange-100 text-orange-800',
          message: 'Your subscription payment failed. Please update your payment method.',
          className: 'border-orange-200'
        };

      default:
        return {
          badge: 'Unknown',
          badgeColor: 'bg-gray-200 text-gray-800',
          message: 'Subscription status unknown',
          className: 'border-gray-200'
        };
    }
  };

  const status = getStatusDisplay();

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`bg-white rounded-lg p-4 shadow-sm border ${status.className}`}
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-medium text-gray-900">Subscription Status</h3>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.badgeColor}`}>
          {status.badge}
        </span>
      </div>
      
      <p className="text-sm text-gray-600 mb-4">{status.message}</p>
      
      {subscription && (
        <div className="mb-4">
          <div className="text-sm text-gray-700 flex justify-between">
            <span>Plan:</span>
            <span className="font-medium">{subscription.plan.name}</span>
          </div>
          
          <div className="text-sm text-gray-700 flex justify-between mt-1">
            <span>Price:</span>
            <span className="font-medium">${subscription.plan.price}/{subscription.plan.interval}</span>
          </div>
          
          {subscription.status === 'active' && (
            <div className="text-sm text-gray-700 flex justify-between mt-1">
              <span>Next billing date:</span>
              <span className="font-medium">{formatDate(subscription.endDate)}</span>
            </div>
          )}
        </div>
      )}
      
      <div className="flex space-x-2">
        {!subscription || subscription.status !== 'active' ? (
          <button 
            onClick={() => window.location.href = '#pro'}
            className="text-sm px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors w-full"
          >
            Upgrade to Pro
          </button>
        ) : (
          <>
            <button 
              onClick={() => setShowManager(true)}
              className="text-sm px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex-1"
            >
              Manage Subscription
            </button>
          </>
        )}
      </div>

      {/* Subscription Manager Modal */}
      {showManager && subscription && (
        <SubscriptionManager
          isOpen={showManager}
          onClose={() => setShowManager(false)}
          subscription={subscription}
          onUpdate={handleSubscriptionChange}
        />
      )}
    </motion.div>
  );
}

export default SubscriptionStatus;
